import _ from 'lodash';
// import { generateShortUUID } from '@skygroup/shared/utils/generateRandomString';

const isNullishOrEmptyObject = (val: any) =>
  val == null ||
  (typeof val === 'object' &&
    !Array.isArray(val) &&
    Object.keys(val).length === 0);

export function replaceStringElementInArr({
  arr,
  oldVal,
  newVal,
}: {
  arr: string[];
  oldVal: string;
  newVal: string;
}): string[] {
  return arr.map((item) => (item === oldVal ? newVal : item));
}

export function insertAt(array: any, index: number, value: string) {
  return [..._.slice(array, 0, index), value, ..._.slice(array, index)];
}

export function replaceAt(array: any[], index: number, value: any): any[] {
  let newArray = [...array];
  newArray[index] = value;
  return newArray;
}

export function pagesWithSamePath(
  path1: string | string[],
  path2: string | string[]
): boolean {
  if (typeof path1 == 'string' && typeof path2 == 'string') {
    return path1 === path2;
  }
  if (
    path1.length !== path2.length ||
    !Array.isArray(path1) ||
    !Array.isArray(path2)
  ) {
    return false;
  }
  return path1.sort().join() === path2.sort().join();
}

// export function deepMerge(target, source) {
//   for (const key in source) {
//     // Handle 'theme' key specifically
//     if (key === 'theme') {
//       target[key] = source[key];
//       continue;
//     }
//     if (
//       source[key] &&
//       typeof source[key] === 'object' &&
//       !Array.isArray(source[key])
//     ) {
//       if (!target[key]) {
//         target[key] = {};
//       }
//       deepMerge(target[key], source[key]);
//     } else if (source[key]) {
//       target[key] = source[key];
//     }
//   }
//   return target;
// }

export function updateWhiteLabelPage({
  pagesArr,
  oldVal,
  newVal,
  selectedPageId,
  siteDefaultConfigPages,
}: {
  pagesArr: any[];
  oldVal: string;
  newVal: string;
  selectedPageId: string;
  siteDefaultConfigPages: any[];
}) {
  let pageUpdatedModules = null;
  const updatedPages = pagesArr?.map((page) => {
    if (page.id !== selectedPageId && page.name !== selectedPageId) {
      return page;
    }

    pageUpdatedModules = replaceStringElementInArr({
      arr: page.modules,
      oldVal,
      newVal,
    });

    return {
      ...page,
      modules: pageUpdatedModules,
    };
  });

  return { updatedPages, pageUpdatedModules };
}

export function attachNewPageToWhiteLabel({
  pagesArr,
  oldVal,
  newVal,
  selectedPageId,
  siteDefaultConfigPages,
}: {
  pagesArr: any[];
  oldVal: string;
  newVal: string;
  selectedPageId: string;
  siteDefaultConfigPages: any[];
}) {
  const newPageElement = siteDefaultConfigPages?.find(
    (page) => page.id === selectedPageId || page.name === selectedPageId
  );
  if (!newPageElement) {
    return { updatedPages: pagesArr, pageUpdatedModules: null };
  }

  const pageUpdatedModules = replaceStringElementInArr({
    arr: newPageElement.modules,
    oldVal,
    newVal,
  });

  return {
    updatedPages: [
      ...pagesArr,
      {
        ...newPageElement,
        modules: pageUpdatedModules,
      },
    ],
    pageUpdatedModules,
  };
}

export function updateNestedValue(obj: any, path: string[], value: any): any {
  if (path.length === 0) return obj; // If path is empty, return original object
  const [firstKey, ...restKeys] = path;
  return {
    ...(obj || {}),
    [firstKey]: restKeys?.length
      ? updateNestedValue(obj?.[firstKey] || {}, restKeys, value) // Recursively update deeper
      : value, // Set new value at final depth
  };
}

export interface NewSelectedModuleType {
  index: number;
  moduleName: string;
  instance: string;
  pageUpdatedModules: string[] | [];
  newDupInstance: any;
}
export interface HandleCreateDupInstanceReturnType {
  finalValue: any;
  newSelectedModule: NewSelectedModuleType;
}

export function handleCreateDupInstanceType({
  prev,
  currSiteDefaultConfig,
  // path,
  // value,
  iframeWindow,
  selectedModuleInfo,
  selectedPage,
  siteDefaultConfig,
}: {
  prev: any;
  currSiteDefaultConfig: any;
  iframeWindow: any;
  selectedModuleInfo: any;
  selectedPage: any;
  siteDefaultConfig: any;
}): HandleCreateDupInstanceReturnType {
  // if instance does not exist within WhiteLabel take and use it from "currSiteDefaultConfig"
  // and also update pages likewise + do iframe.postMessage for "CreateDupInstance"
  const isLayoutModule = !!selectedModuleInfo?.layoutSection;
  const original_instance =
    selectedModuleInfo?.instance || selectedModuleInfo?.module;
  const newInstance = original_instance + '_dup';

  const newDupInstance = {
    ...currSiteDefaultConfig,
    original_instance,
    instance: newInstance,
  };

  const whiteLabelIncludesSelectedPage = prev?.pages?.some(
    (page: any) =>
      (page.id && page.id === selectedPage.id) ||
      page.name === selectedPage.name
  );

  let updatedLayouts, updatedPages, pageUpdatedModules;

  if (isLayoutModule) {
    const layoutsArr = prev?.layouts?.length
      ? prev.layouts
      : siteDefaultConfig.layouts;

    updatedLayouts = handleUpdateLayoutModules({
      prev,
      renderedLayoutArr:
        layoutsArr?.[selectedPage?.layout]?.[selectedModuleInfo?.layoutSection],
      selectedPage,
      selectedModuleInfo,
      newDupInstance,
    });
  } else {
    const helperFunc = whiteLabelIncludesSelectedPage
      ? updateWhiteLabelPage
      : attachNewPageToWhiteLabel;

    const helperProps = {
      pagesArr: prev?.pages,
      oldVal: newDupInstance.original_instance,
      newVal: newDupInstance.instance,
      selectedPageId: selectedPage.id,
      siteDefaultConfigPages: siteDefaultConfig?.pages,
    };

    const result: {
      updatedPages: any[];
      pageUpdatedModules: string[] | null;
    } = helperFunc(helperProps);

    updatedPages = result.updatedPages;
    pageUpdatedModules = result.pageUpdatedModules;
  }

  const finalValue = {
    ...prev,
    modulesConfigs: {
      ...prev.modulesConfigs,
      [newDupInstance?.instance]: newDupInstance,
    },
    ...(isLayoutModule ? { layouts: updatedLayouts } : { pages: updatedPages }),
  };

  const pageId = selectedPage.id;

  const replaceIndex = selectedPage?.modules?.findIndex(
    (instance: string) => instance === newDupInstance?.original_instance
  );

  iframeWindow?.postMessage(
    {
      type: 'CreateDupInstance',
      pageId: pageId,
      replaceIndex: replaceIndex,
      selectedPage: selectedPage,
      selectedModuleInfo: selectedModuleInfo,
      selectedModuleUpdated: {
        index: replaceIndex,
        moduleName: newDupInstance.module,
        instance: newDupInstance.instance,
        newDupInstance,
        pageUpdatedModules,
      },
      dupModuleObj: newDupInstance,
    },
    '*'
  );

  return {
    finalValue,
    newSelectedModule: {
      index: replaceIndex,
      moduleName: newDupInstance.module,
      instance: newDupInstance.instance,
      newDupInstance: newDupInstance,
      pageUpdatedModules: pageUpdatedModules || [],
    },
  };
}

export interface HandleEditModuleReturnType {
  finalValue: any;
  newSelectedModule: null;
}

export function handleEditModule({
  prev,
  // currWhiteLabelConfig,
  // currSiteDefaultConfig,
  path,
  value,
  iframeWindow,
  selectedModuleInfo,
}: {
  prev: any;
  path: string[];
  value: any;
  iframeWindow: any;
  selectedModuleInfo: any;
}): HandleEditModuleReturnType {
  // if instance exists within WhiteLabel just update its state + do iframe.postMessage for "EditModule"
  const currWhiteLabelConfig =
    prev.modulesConfigs[selectedModuleInfo?.instance];

  const updatedWhiteLabelConfig = updateNestedValue(
    currWhiteLabelConfig,
    path,
    value
  );
  const finalValue = {
    ...prev,
    modulesConfigs: {
      ...prev.modulesConfigs,
      [selectedModuleInfo?.instance]: updatedWhiteLabelConfig,
    },
  };

  // const updatedConfig = updateNestedValue(currSiteDefaultConfig, path, value);
  iframeWindow?.postMessage(
    {
      type: 'EditModule',
      [selectedModuleInfo?.instance]: updatedWhiteLabelConfig,
    },
    '*'
  );

  return { finalValue, newSelectedModule: null };
}

export function handleUpdatePageModules({
  prev,
  renderedLayoutArr,
  selectedPage,
  selectedModuleInfo,
  newDupInstance,
}: {
  prev: any; // configInterface;
  renderedLayoutArr: string[];
  selectedPage: pagesInterface;
  selectedModuleInfo: {
    moduleName: string;
    instance: string;
    layoutSection?: string;
    pageId?: string;
    index?: number;
  };
  newDupInstance: any;
}) {
  const updatedPages = prev.pages.map((page: any) => {
    if (
      page.allow_edit !== false &&
      ((page.id && page.id === selectedPage.id) ||
        pagesWithSamePath(page.path, selectedPage.path))
    ) {
      const updatedPageModules = replaceStringElementInArr({
        arr: page.modules,
        oldVal: newDupInstance.original_instance,
        newVal: newDupInstance.instance,
      });
      return {
        ...page,
        modules: updatedPageModules,
        id: selectedPage.id,
      };
    }
    return page;
  });

  return updatedPages;
}

// Later import it from shared !!!!
interface pagesInterface {
  id: string;
  name: string;
  path: string;
  modules: string[];
  layout: string;
  type: string;
  seo: {
    title: string;
    desc: string;
  };
  allow_edit?: boolean;
}

export function handleUpdateLayoutModules({
  prev,
  renderedLayoutArr,
  selectedPage,
  selectedModuleInfo,
  newDupInstance,
}: {
  prev: any; // configInterface;
  renderedLayoutArr: string[];
  selectedPage: pagesInterface;
  selectedModuleInfo: {
    moduleName: string;
    instance: string;
    layoutSection?: string;
    pageId?: string;
    index?: number;
  };
  newDupInstance: any;
}) {
  const updatedLayoutSection = !isNullishOrEmptyObject(prev?.layouts)
    ? replaceStringElementInArr({
        arr: renderedLayoutArr,
        oldVal: newDupInstance.original_instance,
        newVal: newDupInstance.instance,
      })
    : [newDupInstance.instance];

  if (!selectedModuleInfo?.layoutSection) {
    return prev?.layouts;
  }

  const updatedLayouts = {
    ...(prev?.layouts ?? {}),
    [selectedPage?.layout]: {
      ...prev?.layouts?.[selectedPage?.layout],
      [selectedModuleInfo?.layoutSection]: updatedLayoutSection,
    },
  };

  return updatedLayouts;
}
